/**
 * IL CONTATTO IN BACHECA — sezione `board` del suo `EntityTypeDescriptor` (TS-117).
 *
 * È il gemello di `taskBoard` e `quotationBoard`, e la somiglianza è il punto: mettere un'entità in
 * bacheca è scrivere **questo file** e registrarlo. Niente colonne, niente DDL, niente pagina
 * dedicata — la pagina della bacheca non conosce il contatto.
 *
 * I contatti si caricano dal REST **del contatto** con `?kanbanId=`: è il server a risolvere quali
 * appartengono alla board, e il DTO che torna è quello pieno, così la card ha località, stato lead e
 * recapiti senza chiedere altro.
 *
 * <b>`currentStateId` non è un campo del contatto a database</b>, è una riga di
 * `PipelineMembership` che il server risolve prima di rispondere. Da qui non si vede la differenza,
 * ed è voluto: la bacheca chiede «in che colonna sei», non «dove tieni quel dato». E non è il
 * `leadStatus`: sono due assi indipendenti, e lo stesso contatto può stare in pipeline diverse.
 *
 * `personApi` si importa **dinamicamente**, come negli altri due board e come fanno `resolveLabel` e
 * `search` nel registro: il modulo api non deve finire nel percorso di boot, altrimenti si riapre il
 * ciclo di import che `coreEntityRegistration` evita per costruzione. La card invece è un import
 * statico: disegna e basta, e non torna indietro al registro.
 */

import type { EntityTypeDescriptor, Page } from '@crm/contracts';
import { PersonKanbanCard } from './PersonKanbanCard';
import type { Person } from './personMappers';

type BoardSection = NonNullable<EntityTypeDescriptor['board']>;

/** Il descrittore parla di record generici; qui sotto sono contatti, e il cast sta tutto qui. */
const asPerson = (item: Record<string, unknown>) => item as unknown as Person;

export const personBoard: BoardSection = {
  // pageSize alto e non paginazione: la bacheca mostra tutte le colonne insieme, e una card mancante
  // non si distingue da una card filtrata via.
  loadItems: async (kanbanId, groupId) => {
    const { personApi } = await import('./personApi');
    const page = await personApi.getAll(groupId, { kanbanId, pageSize: 1000 });

    return page as unknown as Page<Record<string, unknown>>;
  },

  idOf: (item) => asPerson(item).personId,

  stateIdOf: (item) => asPerson(item).currentStateId ?? 0,

  move: async (id, pipelineStateId) => {
    const { personApi } = await import('./personApi');

    await personApi.update({ personId: id, currentStateId: pipelineStateId });
  },

  Card: ({ item }) => <PersonKanbanCard person={asPerson(item)} />,
};
